<template>
  <div>
    <div class="font-bold">Coming Soon</div>
    <div class="flex justify-between space-x-5 hidden">
      <div class="w-full">
        <div class="mb-2 w-full text-grey-900">
          <div class="w-full font-semibold text-left title">
            Title (Required)
          </div>
          <div class="w-full text-sm h-12 text-left">
            <text-input
              v-model="video.name"
              :validator="$v.video.name"
              attribute="Name"
              placeholder="Name"
            />
          </div>
        </div>
        <div class="mb-2 w-full text-grey-900">
          <div class="w-full font-semibold text-left title">Category</div>
          <div class="w-full text-sm text-left">
            <div>
              <gd-dropdown
                :options="categoryDropdownOptions"
                v-model="video.category_id"
                :validator="$v.video.category_id"
                value="video.category_id"
                class="text-left"
                :placeholder="$t('frontend_groovevideo_create_select_category')"
                size="full"
              ></gd-dropdown>
            </div>
          </div>
        </div>
        <div class="mt-5 w-full">
          <gd-button
            @click="syncToDB()"
            primary
            :disabled="$v.video.$invalid"
            :class="[$v.video.$invalid ? 'pointer-events-none' : '']"
            >Upload</gd-button
          >
        </div>
      </div>

      <div class="w-full">
        <div
          class="mb-2 w-full text-grey-900"
          :class="[displayUploadbox ? 'hidden' : 'inline-block']"
        >
          <div class="w-full font-semibold text-left title">
            Drag and drop video files to upload
          </div>
          <div>
            <vue-dropzone
              id="myVueDropzoneId"
              ref="myVueDropzoneRef"
              :options="dropzoneOptions"
              :use-custom-slot="true"
              :awss3="awss3"
              @vdropzone-s3-upload-error="s3UploadErrorCallback"
              @vdropzone-s3-upload-success="s3UploadSuccessCallback"
              @vdropzone-upload-progress="uploadProgress"
              @vdropzone-total-upload-progress="totalProgress"
              @vdropzone-file-added="fileAdded"
            >
              <div class="dropzone-custom-content space-y-5">
                <div>
                  Upload file from your computer. You can upload
                  <em>{{
                    dropzoneOptions.acceptedFiles
                      .split(',.')
                      .join(', ')
                      .split('.')
                      .join('')
                  }}</em>
                  file formats
                </div>
                <div class="bg-grey-200 rounded-lg p-5">
                  <div>Drag &amp; Drop File Here</div>
                  <div>-OR-</div>
                  <div
                    class="
                      w-full
                      py-4
                      flex flex-row
                      items-center
                      justify-center
                    "
                  >
                    <gd-button primary size="large">Select File</gd-button>
                  </div>
                  <div class="text-red text-sm">
                    {{ `Allowed Max File Size is ${maxSize}MB` }}
                  </div>
                </div>
              </div>
            </vue-dropzone>
          </div>
        </div>
        <div
          class="dz-preview dz-file-preview flex justify-center mt-32"
          :class="[!displayUploadbox ? 'hidden' : 'inline-block']"
        >
          <div class="preview text-center">
            <div :class="'p' + uploadProgressStatus">
              <span>{{ uploadProgressStatus }} %</span>
              <div class="slice">
                <div class="bar"></div>
                <div class="fill"></div>
              </div>
            </div>
            <h6 class="mt-3 w-full inline-block">Uploading...</h6>
            <p class="mt-2">Wait until your video is uploaded</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import api from '@groovepages/gd-lib/api';
import vue2Dropzone from 'vue2-dropzone';
import GdButton from '../Button';
import mixins from '../../mixins';
import 'vue2-dropzone/dist/vue2Dropzone.min.css';
import { required, url, minLength, maxLength } from 'vuelidate/lib/validators';

export default {
  name: 'VideoUploader',
  mixins: [mixins],
  props: {
    dark: {},
    maxSize: {
      default: 200,
    },
  },
  data() {
    return {
      uploadProgressStatus: 0,
      displayUploadbox: false,
      categories: [],
      video: {
        name: 'default-name',
        category_id: 1,
        video_source: '',
      },
      dropzoneOptions: {
        acceptedFiles: '.mp4,.flv,.mov,.m4v',
        url: `${window.config.apiCoreUrl}/groovevideo/upload`,
        maxFiles: 1,
        maxFilesize: this.maxSize,
        autoProcessQueue: false,
        autoQueue: true,
        chunking: false, // it is kept false because it is not supported and gives error
        chunkSize: 500, // bytes
        thumbnailWidth: 150, // px
        thumbnailHeight: 150,
        addRemoveLinks: false,
      },
      awss3: {
        signingURL: `${window.config.apiCoreUrl}/groovevideo/get-signing-url`,
        headers: {},
        params: {},
        sendFileToServer: false,
        withCredentials: false,
      },
    };
  },
  validations: {
    video: {
      name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(200),
      },
      category_id: {
        required,
      },
      video_source: {
        required,
        url,
      },
    },
  },
  computed: {
    categoryDropdownOptions() {
      if (this.categories.length < 1) {
        return [];
      }

      return this.categories.map((category) => {
        return { text: category.category, value: category.id };
      });
    },
  },
  components: {
    GdButton,
    vueDropzone: vue2Dropzone,
  },
  async mounted() {
    console.log('mounted, now fetching categories');
    await this.getCategories();
    return false;
  },
  methods: {
    async syncToDB() {
      this.$v.$touch();
      try {
        if (this.$v.$invalid) {
          throw new Error('Invalid Data');
        }

        await api.post(
          `${window.config.apiCoreUrl}/groovevideo/videos`,
          { ...this.video },
          true
        );

        this.$emit('sendBackToLibrary', true);
      } catch (err) {
        /** */
      }
    },
    async getCategories() {
      try {
        this.categories = await api
          .get(`${window.config.apiCoreUrl}/groovevideo/category`)
          .then((d) => d.data);
      } catch (err) {
        /** */
      }
    },
    s3UploadErrorCallback(e) {
      console.log(e);
    },
    s3UploadSuccessCallback(videoSource) {
      this.video.video_source = videoSource;
    },
    uploadProgress(file, progress, bytesSent) {
      this.uploadProgressStatus = progress;
      this.uploadProgressStatus = Math.round(this.uploadProgressStatus);
    },
    totalProgress(totaluploadprogress, totalBytes, totalBytesSent) {},
    fileAdded(file) {
      try {
        const fileSize = (file.size / (1000 * 1000)).toFixed(1);

        setTimeout(() => {
          if (!this.$refs.myVueDropzoneRef.getQueuedFiles().length) {
            throw new Error('No file found. Please retry.');
          }
        }, 300);

        if (fileSize > this.maxSize) {
          throw new Error(
            `Files larger than ${this.maxSize}MB are not allowed.`
          );
        }

        setTimeout(() => {
          if (
            this.$refs.myVueDropzoneRef.$el.classList.contains('dz-started')
          ) {
            this.displayUploadbox = true;
          }
          this.$refs.myVueDropzoneRef.processQueue();
        }, 300);
      } catch (err) {
        this.$refs.myVueDropzoneRef.removeFile(file);
      }
    },
    removeAllFiles() {
      this.$refs.myVueDropzoneRef.removeAllFiles();
    },
  },
};
</script>